import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { UnitsProvider } from "@/contexts/UnitsContext";
import RequireAuth from "@/components/RequireAuth";
import Index from "./pages/Index.tsx";

const BoxCalculator = lazy(() => import("./pages/BoxCalculator.tsx"));
const AIChatPage = lazy(() => import("./pages/AIChatPage.tsx"));
const ProductsPage = lazy(() => import("./pages/ProductsPage.tsx"));
const WiringPage = lazy(() => import("./pages/WiringPage.tsx"));
const AuthPage = lazy(() => import("./pages/AuthPage.tsx"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage.tsx"));
const SavedItemsPage = lazy(() => import("./pages/SavedItemsPage.tsx"));
const ProfilePage = lazy(() => import("./pages/ProfilePage.tsx"));
const PricingPage = lazy(() => import("./pages/PricingPage.tsx"));
const ElectricalPage = lazy(() => import("./pages/ElectricalPage.tsx"));
const SubscriptionSuccessPage = lazy(() => import("./pages/SubscriptionSuccessPage.tsx"));
const AdminCalibrationPage = lazy(() => import("./pages/AdminCalibrationPage.tsx"));
const AdminAuditPage = lazy(() => import("./pages/AdminAuditPage.tsx"));
const OAuthConsentPage = lazy(() => import("./pages/OAuthConsentPage.tsx"));
const BlogPage = lazy(() => import("./pages/BlogPage.tsx"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage.tsx"));
const InstallersPage = lazy(() => import("./pages/InstallersPage.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

const queryClient = new QueryClient();

const P = ({ children }: { children: React.ReactNode }) => (
  <RequireAuth>{children}</RequireAuth>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <UnitsProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              {/* Public routes */}
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/.lovable/oauth/consent" element={<OAuthConsentPage />} />

              {/* Public tool routes — app is free, keep crawlable */}
              <Route path="/" element={<Index />} />
              <Route path="/box-calculator" element={<BoxCalculator />} />
              <Route path="/ai-chat" element={<AIChatPage />} />
              <Route path="/ai" element={<Navigate to="/ai-chat" replace />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/wiring" element={<WiringPage />} />
              <Route path="/electrical" element={<ElectricalPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/for-installers" element={<InstallersPage />} />

              {/* Protected account routes */}
              <Route path="/saved" element={<P><SavedItemsPage /></P>} />
              <Route path="/profile" element={<P><ProfilePage /></P>} />
              <Route path="/subscription-success" element={<P><SubscriptionSuccessPage /></P>} />
              <Route path="/admin/calibration" element={<P><AdminCalibrationPage /></P>} />
              <Route path="/admin/audit" element={<P><AdminAuditPage /></P>} />

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
      </UnitsProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
